import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { t } from '@aquilixshop/i18n';
import { escapeHtml } from '../../sections/_shared/settingValue.js';
import { marginFields, SCHEME_COLOR_OPTIONS, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, schemeColor, str, visibilityCss } from '../_shared/blockRender.js';
import { inlineMessageHtml } from '../_shared/inlineMessage.js';
import { useProductState } from '../_shared/productContext.js';
/**
 * Shrine `urgency` (scarcity / "HOT PRODUCT | LOW STOCK") — config-by-config. Shrine ships EXACTLY six
 * settings on this block and we reproduce all six, grouped exactly as Shrine groups them:
 *  - `visibility`   (top-level "Display on") → visibilityField.
 *  - `urgency`      (top-level "Text") → the actual displayed scarcity message (Shrine ships the
 *                   pipe-joined default 'HOT PRODUCT | LOW STOCK'; the pipe is a visual divider we
 *                   render as a vertical rule between segments, exactly as Shrine's theme does).
 *  - `color_scheme` (top-level "Color") → mapped to a `--aqx-scheme-*` var via schemeColor(); drives
 *                   the icon + text colour (Shopify color-scheme naming, generic mapping).
 *  - `[header]`     (Margins divider, UI-only, no id) → represented by the marginFields group header.
 *  - `margin_top` / `margin_bottom` (Margins) → marginFields(15, 15).
 *
 * ID MAPPING (our id ← Shrine id, kept stable): urgency←urgency, colorScheme←color_scheme.
 *
 * SECTION-SPECIFIC behaviour (the brief: "Only {n} left in stock!" / "Selling fast", icon, optional
 * pulse, colour) layered on top of the faithful schema WITHOUT inventing Shrine controls:
 *  - ICON: a small inline flame SVG (NOT the Material-Symbols glyph font) precedes the message.
 *  - THRESHOLD / "{n}": when the bound product tracks inventory and has a finite low count, any `{n}`
 *    token in the message is substituted with the real remaining-stock number, so "Only {n} left in
 *    stock!" becomes real scarcity. With no tracked stock (demo product, untracked SKUs) the literal
 *    authored message renders unchanged — degrades cleanly, SSR-correct on first paint.
 *  - PULSE: a subtle decorative pulse on the icon. It is CSS-only (`@keyframes`), runs without JS, and
 *    is gated behind `prefers-reduced-motion: reduce` so it disables for motion-sensitive users. Shrine
 *    has no pulse control, so it is always-on decoration rather than a fabricated setting.
 *
 * DEFERRED (justified): none — every Shrine setting is implemented and wired. (No Shopify-only settings
 * exist on this block: no reveal/scroll animation, no Material-Symbols glyph, no CDN srcset, no
 * liquid/app hook, no live cart/checkout coupling.)
 *
 * BEYOND SHRINE (founder, 24/09/2026 — « 🔥 ONLY 12 LEFT IN STOCK! », the number in orange, the rest in
 * red, the whole line blinking):
 *  - `urgency` is RICH text now. A seller selects a word in the inspector and gives it its own colour
 *    (a `<span style="color:…">`, which the shared richtext policy already allows). The stored value of
 *    every existing store is still a plain STRING — and so is what the AI composer writes — so both
 *    shapes render: a string is escaped and shown verbatim, never parsed as markup.
 *  - `textBlink` + `blinkSpeed` fade the TEXT in and out (pure CSS keyframes, SSR-safe, no JS), switched
 *    off under `prefers-reduced-motion: reduce`. The flame keeps its own pulse. These are NOT the shared
 *    `textAnimation` field (split/blur/typing, driven by the storefront's attribute engine): a blink is a
 *    loop, not a reveal, and it must not promise attributes that engine would then look for.
 */
export const urgencyBlockSchema = {
    type: 'urgency',
    name: 'Urgency',
    max: 1,
    fields: [
        visibilityField,
        {
            id: 'urgency',
            type: 'richtext',
            label: 'Text',
            info: 'Scarcity message. Select a word and pick its colour in the toolbar. Use {n} to insert the ' +
                'remaining stock count when inventory is tracked, and | to separate two mentions.',
            default: { format: 'html', html: 'PRODUIT POPULAIRE | STOCK LIMITÉ' },
            defaultKey: 'product.urgency_banner',
            // ÉCRITE PAR L'IA : la ligne d'urgence dit ce qui est vrai de CE produit. Le brief lui
            // interdit d'inventer un stock ou un compte à rebours ; le champ reste vide si elle n'a rien.
            aiWritable: true,
        },
        {
            id: 'colorScheme',
            aiConfigurable: true,
            type: 'select',
            label: 'Color',
            default: 'accent-1',
            options: SCHEME_COLOR_OPTIONS,
        },
        {
            id: 'textBlink',
            type: 'checkbox',
            label: 'Blink the text',
            group: 'Animations',
            info: 'The text fades in and out continuously. Off for visitors who asked for reduced motion.',
            default: false,
        },
        {
            id: 'blinkSpeed',
            type: 'range',
            label: 'Blink speed',
            group: 'Animations',
            info: 'Duration of one on-off cycle.',
            min: 300,
            max: 3000,
            step: 100,
            default: 1000,
            unit: 'ms',
            visibleWhen: { field: 'textBlink', equals: true },
        },
        ...marginFields(15, 15),
    ],
};
/** Small inline flame icon (Shrine renders an SVG glyph, NOT the Material-Symbols font). */
function FlameIcon({ color }) {
    const svg = {
        width: '1.05em',
        height: '1.05em',
        flexShrink: 0,
        fill: color,
        display: 'inline-block',
    };
    return (_jsx("svg", { viewBox: "0 0 384 512", "aria-hidden": "true", style: svg, children: _jsx("path", { d: "M216 23.86c0-23.8-30.65-32.77-44.15-13.04C48 191.85 224 200 224 288c0 35.63-29.11 64.46-64.85 63.99-35.17-.45-63.15-29.77-63.15-64.94v-85.51c0-21.7-26.47-32.23-41.43-16.5C27.8 213.16 0 261.33 0 320c0 105.87 86.13 192 192 192s192-86.13 192-192c0-170.29-168-193-168-296.14z" }) }));
}
/**
 * Resolve the remaining-stock count for `{n}` substitution. Returns null when inventory is untracked or
 * has no finite quantity (so the literal message renders unchanged). SSR-safe: derived purely from the
 * resolved product, identical on server + client.
 */
function remainingStock(inventory) {
    if (inventory && typeof inventory === 'object') {
        const inv = inventory;
        if (inv.tracked === true && typeof inv.quantity === 'number' && inv.quantity >= 0) {
            return inv.quantity;
        }
    }
    return null;
}
/**
 * The Shrine `|` divider, INSERTED where the pipe stood instead of cutting the HTML into segments: a pipe
 * inside a coloured run (`<span style="color:…">CHAUD | STOCK</span>`) must not leave one segment with an
 * unclosed span and the next with a stray closing one. The lookahead skips a pipe inside a tag (none of
 * the allowed attributes carries one in practice, but a URL may), and the surrounding spaces go with it —
 * the divider brings its own margins.
 */
const DIVIDER = '<span class="aqx-urg-div" aria-hidden="true"></span>';
const PIPE_OUTSIDE_TAGS = /\s*\|(?![^<]*>)\s*/g;
/**
 * Scarcity / urgency line: pulsing flame icon + message, both in the chosen color-scheme colour. The
 * message may carry a `{n}` token (real stock), a `|` pipe (rendered as a vertical divider, matching
 * Shrine's "HOT PRODUCT | LOW STOCK" default) and, since it is rich text, a colour per word.
 */
const UrgencyBlock = ({ block, context }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-urg-${block.id}`;
    const color = schemeColor(str(bs.colorScheme, 'accent-1'));
    // Resolve the message: substitute {n} with the real remaining stock when tracked, else strip the token.
    const raw = inlineMessageHtml(bs.urgency) || escapeHtml(t(context, 'product.urgency_banner'));
    const stock = remainingStock(ps.product.inventory);
    const resolved = stock !== null ? raw.replace(/\{n\}/g, String(stock)) : raw.replace(/\s*\{n\}\s*/g, ' ').trim();
    const html = resolved.replace(PIPE_OUTSIDE_TAGS, DIVIDER);
    const blink = bs.textBlink === true;
    const blinkSpeed = num(bs.blinkSpeed, 1000);
    const vis = visibilityCss(cls, bs.visibility);
    // Scoped style: ONE block — pulse keyframes (reduced-motion-safe) + the divider rule + the optional
    // text blink. A seller's coloured span sits INSIDE `.aqx-urg-txt` and wins over the block colour by
    // being the closer rule (inline style), so the rest of the line keeps the scheme colour.
    const css = `.${cls}{display:flex;align-items:center;gap:.5em;` +
        `font-size:var(--aqx-theme-text-sm,14px);font-weight:600;line-height:1.3;color:${color}}` +
        `.${cls} .aqx-urg-ico{display:inline-flex;transform-origin:center bottom}` +
        `.${cls} .aqx-urg-txt{display:inline}` +
        `.${cls} .aqx-urg-div{display:inline-block;width:1px;height:1em;margin:0 .55em;` +
        `vertical-align:-.1em;background:currentColor;opacity:.45}` +
        `@keyframes aqx-urg-pulse-${block.id}{0%,100%{transform:scale(1)}50%{transform:scale(1.18)}}` +
        `@media(prefers-reduced-motion:no-preference){.${cls} .aqx-urg-ico{` +
        `animation:aqx-urg-pulse-${block.id} 1.2s ease-in-out infinite}}` +
        (blink
            ? `.${cls} .aqx-urg-txt--blink{animation:aqx-urg-blink-${block.id} ${blinkSpeed}ms ease-in-out infinite}` +
                `@keyframes aqx-urg-blink-${block.id}{0%,100%{opacity:1}50%{opacity:.12}}` +
                `@media(prefers-reduced-motion:reduce){.${cls} .aqx-urg-txt--blink{animation:none}}`
            : '');
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css + vis } }), _jsxs("div", { className: cls, role: "status", style: marginStyle(bs), children: [_jsx("span", { className: "aqx-urg-ico", "aria-hidden": "true", children: _jsx(FlameIcon, { color: color }) }), _jsx("span", { className: blink ? 'aqx-urg-txt aqx-urg-txt--blink' : 'aqx-urg-txt', dangerouslySetInnerHTML: { __html: html } })] })] }));
};
export const urgencyBlock = {
    type: 'urgency',
    schema: urgencyBlockSchema,
    Component: UrgencyBlock,
    meta: { label: 'Urgency' },
};
//# sourceMappingURL=index.js.map